import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface OpenPO {
  poNumber: string;
  qtyOrdered: number;
  qtyReceived: number;
  expectedDate: string | null;
  poId: string;
}

interface ShortageItem {
  partNumber: string;
  description: string;
  inventoryItemId: string | null;
  currentStock: number;
  totalNeeded: number;
  shortage: number;
  unit: string;
  supplierName: string | null;
  supplierPartNumber: string | null;
  reorderQuantity: number;
  minQuantity: number;
  leadTimeDays: number;
  imageFilename: string | null;
  inventoryGroup: string | null;
  unitCost: number;
  // Roll/linear ordering info
  stockTakeType: string;
  stockLength: number;
  displayUnit: string;
  conversionFactor: number;
  purchasePrice: number | null;
  affectedOrders: string[];
  affectedOrderCount: number;
  onOrder: number;
  openPOs: OpenPO[];
  needToOrder: number;
  // Predictability
  consumed90d: number;
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  projectedRunoutDays: number | null;
  projectedRunoutDate: string | null;
  orderByDate: string | null;
  urgency: 'critical' | 'warning' | 'ok';
  // Smart recommendations
  orderQty30d: number;
  orderQty60d: number;
  orderQty90d: number;
  suggestedQty: number;
  suggestedLastsDays: number | null;
  nextReorderDays: number | null;
}

interface ShortageSummary {
  totalShortageItems: number;
  totalOrdersAffected: number;
  items: ShortageItem[];
}

// Per-item overrides
interface LineOverride {
  qty?: number;
  unitCost?: number;
  days?: number;
  leadTime?: number;
}

async function apiRequest(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Request failed: ${res.status}`);
  }
  return res.json();
}

const DAY_OPTIONS = [30, 60, 90, 120];

export default function WhatToOrderPage() {
  const [data, setData] = useState<ShortageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groupBy, setGroupBy] = useState<'supplier' | 'none' | 'group'>('supplier');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showOnlyUnordered, setShowOnlyUnordered] = useState(false);
  const [globalDays, setGlobalDays] = useState(30);
  // Per-item overrides
  const [overrides, setOverrides] = useState<Record<string, LineOverride>>({});
  // Hidden/removed items
  const [hiddenParts, setHiddenParts] = useState<Set<string>>(new Set());
  // Manually added items
  const [manualItems, setManualItems] = useState<ShortageItem[]>([]);
  // Add item modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState<any[]>([]);
  const [addSearching, setAddSearching] = useState(false);
  // Items excluded from PO (unchecked = excluded)
  const [excludedFromPO, setExcludedFromPO] = useState<Set<string>>(new Set());
  // Tabs
  const [activeTab, setActiveTab] = useState<'shortages' | 'fabric'>('shortages');
  const [fabricData, setFabricData] = useState<any>(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [fabricTab, setFabricTab] = useState<'pre_production' | 'in_production'>('pre_production');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (activeTab === 'fabric' && !fabricData && !fabricLoading) loadFabricData(); }, [activeTab]);

  const loadFabricData = async () => {
    try {
      setFabricLoading(true);
      const result = await api.production.getFabricNeeds();
      setFabricData(result);
    } catch (err: any) {
      console.error('Failed to load fabric needs:', err);
    } finally {
      setFabricLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await api.production.getShortageSummary();
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load shortage data');
    } finally {
      setLoading(false);
    }
  };

  // Get the effective days for a line item
  const getLineDays = (item: ShortageItem) => {
    return overrides[item.partNumber]?.days ?? globalDays;
  };

  // Get the effective lead time for a line item
  const getLeadTime = (item: ShortageItem) => {
    return overrides[item.partNumber]?.leadTime ?? item.leadTimeDays;
  };

  // Is this a roll item (priced per inch, ordered in rolls)?
  const isRollItem = (item: ShortageItem) =>
    item.stockTakeType === 'linear_from_roll' && item.stockLength > 0;

  // Is this an extrusion (priced per stick, ordered in sticks)?
  const isExtrusion = (item: ShortageItem) =>
    (item.stockTakeType === 'cut_length' || item.stockTakeType === 'color_variant') && item.stockLength > 0;

  // Has a purchase_price set (sold in rolls/packs at a fixed price)?
  const hasPurchasePrice = (item: ShortageItem) =>
    item.purchasePrice !== null && item.purchasePrice > 0 && item.stockLength > 0;

  // Bulk = ordered in whole units (rolls/sticks/packs) not individual pieces
  const isBulkItem = (item: ShortageItem) => isRollItem(item) || isExtrusion(item) || hasPurchasePrice(item);

  // Get the order unit label
  const getOrderUnit = (item: ShortageItem) => {
    if (isRollItem(item) || hasPurchasePrice(item)) return 'rolls';
    if (isExtrusion(item)) return 'sticks';
    return '';
  };

  // Calculate order qty — rolls/sticks return whole units, not inches
  const getOrderQty = (item: ShortageItem) => {
    const override = overrides[item.partNumber];
    if (override?.qty !== undefined) return override.qty;
    const days = getLineDays(item);
    const lead = getLeadTime(item);
    let rawQty: number;
    if (item.dailyRate > 0) {
      rawQty = Math.max(item.dailyRate * (days + lead), item.shortage);
    } else {
      rawQty = Math.max(item.shortage, item.reorderQuantity);
    }
    if (isBulkItem(item)) {
      return Math.max(1, Math.ceil(rawQty / item.stockLength));
    }
    return Math.ceil(rawQty);
  };

  // Save lead time to inventory DB
  const saveLeadTime = async (item: ShortageItem, leadTime: number) => {
    if (!item.inventoryItemId) return;
    try {
      await apiRequest(`/inventory/${item.inventoryItemId}`, {
        method: 'PATCH',
        body: JSON.stringify({ leadTimeDays: leadTime }),
      });
    } catch (err) {
      console.error('Failed to save lead time:', err);
    }
  };

  // Search inventory for adding items
  const searchInventory = async (q: string) => {
    if (!q || q.length < 2) { setAddResults([]); return; }
    setAddSearching(true);
    try {
      const items = await apiRequest(`/inventory?search=${encodeURIComponent(q)}&limit=20`);
      const arr = Array.isArray(items) ? items : (items.items || []);
      // Filter out items already in the list
      const existingParts = new Set([
        ...(data?.items || []).map(i => i.partNumber),
        ...manualItems.map(i => i.partNumber),
      ]);
      setAddResults(arr.filter((i: any) => !existingParts.has(i.part_number)));
    } catch (err) {
      console.error('Search failed:', err);
      setAddResults([]);
    } finally {
      setAddSearching(false);
    }
  };

  // Add inventory item to the order list
  const addItemToList = (inv: any) => {
    const item: ShortageItem = {
      partNumber: inv.part_number,
      description: inv.description || '',
      inventoryItemId: inv.id,
      currentStock: inv.quantity_on_hand || 0,
      totalNeeded: 0,
      shortage: 0,
      unit: inv.unit || 'ea',
      supplierName: inv.supplier_name || inv.supplierName || null,
      supplierPartNumber: inv.supplier_part_number || inv.supplierPartNumber || null,
      reorderQuantity: inv.reorder_quantity || inv.reorderQuantity || 0,
      minQuantity: inv.min_quantity || inv.minQuantity || 0,
      leadTimeDays: inv.lead_time_days || inv.leadTimeDays || 0,
      imageFilename: inv.image_filename || inv.imageFilename || null,
      inventoryGroup: inv.inventory_group || inv.inventoryGroup || null,
      unitCost: inv.unit_cost || inv.unitCost || 0,
      stockTakeType: inv.stock_take_type || inv.stockTakeType || 'per_screen',
      stockLength: inv.stock_length || inv.stockLength || 0,
      displayUnit: inv.display_unit || inv.displayUnit || '',
      conversionFactor: inv.conversion_factor || inv.conversionFactor || 1,
      purchasePrice: inv.purchase_price || inv.purchasePrice || null,
      affectedOrders: [],
      affectedOrderCount: 0,
      onOrder: 0,
      openPOs: [],
      needToOrder: 1,
      consumed90d: 0,
      dailyRate: 0,
      weeklyRate: 0,
      monthlyRate: 0,
      projectedRunoutDays: null,
      projectedRunoutDate: null,
      orderByDate: null,
      urgency: 'ok',
      orderQty30d: 0,
      orderQty60d: 0,
      orderQty90d: 0,
      suggestedQty: 0,
      suggestedLastsDays: null,
      nextReorderDays: null,
    };
    setManualItems(prev => [...prev, item]);
    // Set default qty override to reorder quantity or 1
    setOverrides(prev => ({
      ...prev,
      [item.partNumber]: { ...prev[item.partNumber], qty: item.reorderQuantity || 1 },
    }));
    setAddResults(prev => prev.filter(r => r.part_number !== inv.part_number));
  };

  // Unit cost for ordering:
  // - purchase_price set? Use it (actual supplier price per roll/pack)
  // - linear_from_roll? Per-inch × roll length = per-roll price
  // - cut_length/extrusions? unit_cost is already per stick
  // - everything else? unit_cost per each
  const getUnitCost = (item: ShortageItem) => {
    const override = overrides[item.partNumber];
    if (override?.unitCost !== undefined) return override.unitCost;
    if (item.purchasePrice && item.purchasePrice > 0) {
      return item.purchasePrice;
    }
    if (item.stockTakeType === 'linear_from_roll' && item.stockLength > 0) {
      return Math.round(item.unitCost * item.stockLength * 100) / 100;
    }
    return item.unitCost;
  };

  // Get used amount for this line's selected period
  const getUsedAmount = (item: ShortageItem) => {
    const days = getLineDays(item);
    return item.dailyRate > 0 ? Math.round(item.dailyRate * days) : 0;
  };

  // Get "lasts" days for this line — bulk items provide stockLength × qty inches
  const getLastsDays = (item: ShortageItem) => {
    if (item.dailyRate <= 0) return null;
    const qty = getOrderQty(item);
    const totalInches = isBulkItem(item) ? qty * item.stockLength : qty;
    return Math.floor(totalInches / item.dailyRate);
  };

  const setOverrideField = (partNumber: string, field: keyof LineOverride, value: number) => {
    setOverrides(prev => ({
      ...prev,
      [partNumber]: { ...prev[partNumber], [field]: value },
    }));
  };

  // When global days changes, clear per-line day overrides (but keep qty/cost edits)
  const handleGlobalDaysChange = (days: number) => {
    setGlobalDays(days);
    setOverrides(prev => {
      const next: Record<string, LineOverride> = {};
      for (const [k, v] of Object.entries(prev)) {
        const { days: _d, ...rest } = v;
        if (Object.keys(rest).length > 0) next[k] = rest;
      }
      return next;
    });
  };

  const allItems = [...(data?.items || []), ...manualItems];
  const filteredItems = allItems.filter(item => {
    if (hiddenParts.has(item.partNumber)) return false;
    if (showOnlyUnordered && item.needToOrder <= 0) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return item.partNumber.toLowerCase().includes(s) ||
      item.description.toLowerCase().includes(s) ||
      (item.supplierName || '').toLowerCase().includes(s) ||
      (item.inventoryGroup || '').toLowerCase().includes(s);
  });

  const groupedItems = () => {
    if (groupBy === 'none') return [{ label: '', items: filteredItems }];
    const map = new Map<string, ShortageItem[]>();
    for (const item of filteredItems) {
      const key = groupBy === 'group'
        ? (item.inventoryGroup || 'Ungrouped')
        : (item.supplierName || 'No Supplier');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, items]) => ({ label, items }));
  };

  const togglePOInclusion = (partNumber: string) => {
    setExcludedFromPO(prev => {
      const next = new Set(prev);
      if (next.has(partNumber)) {
        next.delete(partNumber);
      } else {
        next.add(partNumber);
      }
      return next;
    });
  };

  // Create PO for a supplier group
  const createPOForSupplier = async (supplierName: string, items: ShortageItem[]) => {
    const orderableItems = items.filter(i => i.needToOrder > 0 && i.inventoryItemId && !excludedFromPO.has(i.partNumber));
    if (orderableItems.length === 0) return;

    setCreating(true);
    try {
      const poItems = orderableItems.map(item => ({
        inventoryItemId: item.inventoryItemId,
        partNumber: item.partNumber,
        description: item.description,
        supplierPartNumber: item.supplierPartNumber || '',
        qtyOrdered: getOrderQty(item),
        unitCost: getUnitCost(item),
      }));

      await apiRequest('/purchase-orders', {
        method: 'POST',
        body: JSON.stringify({
          supplierName: supplierName || 'Unknown',
          notes: `Auto-generated from shortage report — ${new Date().toLocaleDateString()}`,
          items: poItems,
        }),
      });

      await loadData();
      alert(`PO created for ${supplierName} with ${poItems.length} items`);
    } catch (err: any) {
      alert(`Failed to create PO: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Create POs for ALL suppliers at once
  const createAllPOs = async () => {
    const groups = groupedItems().filter(g => g.label && g.items.some(i => i.needToOrder > 0 && i.inventoryItemId));
    if (groups.length === 0) return;
    if (!confirm(`Create ${groups.length} purchase orders for all suppliers with unordered shortages?`)) return;

    setCreating(true);
    let created = 0;
    for (const group of groups) {
      try {
        const orderableItems = group.items.filter(i => i.needToOrder > 0 && i.inventoryItemId && !excludedFromPO.has(i.partNumber));
        if (orderableItems.length === 0) continue;
        await apiRequest('/purchase-orders', {
          method: 'POST',
          body: JSON.stringify({
            supplierName: group.label,
            notes: `Auto-generated from shortage report — ${new Date().toLocaleDateString()}`,
            items: orderableItems.map(item => ({
              inventoryItemId: item.inventoryItemId,
              partNumber: item.partNumber,
              description: item.description,
              supplierPartNumber: item.supplierPartNumber || '',
              qtyOrdered: getOrderQty(item),
              unitCost: getUnitCost(item),
            })),
          }),
        });
        created++;
      } catch (err: any) {
        console.error(`Failed PO for ${group.label}:`, err);
      }
    }
    await loadData();
    alert(`Created ${created} purchase orders`);
    setCreating(false);
  };

  const includedItems = filteredItems.filter(i => !excludedFromPO.has(i.partNumber));
  const totalEstCost = includedItems.reduce((sum, i) => sum + (getOrderQty(i) * getUnitCost(i)), 0);
  const totalUnordered = includedItems.filter(i => i.needToOrder > 0).length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto" />
          <p className="mt-3 text-gray-500">Checking all pipeline orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3 print:text-xl">
            <span className="text-3xl print:text-xl">&#x1F6A8;</span>
            What to Order
          </h1>
          <p className="text-gray-500 mt-1 print:text-xs">
            {data?.totalOrdersAffected || 0} pipeline orders &bull; {data?.totalShortageItems || 0} parts short &bull; {totalUnordered} still need ordering
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button onClick={loadData} className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">
            Refresh
          </button>
          <button onClick={() => window.print()} className="px-3 py-2 text-sm bg-gray-700 text-white hover:bg-gray-800 rounded-lg flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
          {totalUnordered > 0 && (
            <button
              onClick={createAllPOs}
              disabled={creating}
              className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 rounded-lg font-bold disabled:opacity-50 flex items-center gap-1"
            >
              {creating ? 'Creating...' : `Create All POs (${groupedItems().filter(g => g.label && g.items.some(i => i.needToOrder > 0 && !excludedFromPO.has(i.partNumber))).length})`}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 print:hidden">
        <button
          onClick={() => setActiveTab('shortages')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'shortages' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Parts Shortages
        </button>
        <button
          onClick={() => setActiveTab('fabric')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'fabric' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Fabric Needs
        </button>
      </div>

      {activeTab === 'fabric' ? (
        <div>
          {fabricLoading ? (
            <div className="text-center py-12 text-gray-500">Loading fabric needs...</div>
          ) : !fabricData ? (
            <div className="text-center py-12 text-gray-400">No data</div>
          ) : (
            <>
              {/* Fabric sub-tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setFabricTab('pre_production')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg ${
                    fabricTab === 'pre_production' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Pre-Production ({fabricData.totalScreens} screens &bull; {fabricData.totalYards} yds)
                </button>
                <button
                  onClick={() => setFabricTab('in_production')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg ${
                    fabricTab === 'in_production' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  In Production ({fabricData.inProductionScreens} screens &bull; {fabricData.inProductionYards} yds)
                </button>
                <button onClick={loadFabricData} className="ml-auto px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">
                  Refresh
                </button>
              </div>

              {/* Fabric table */}
              {(() => {
                const items = fabricTab === 'pre_production' ? fabricData.fabrics : fabricData.inProduction;
                if (!items || items.length === 0) {
                  return <div className="text-center py-12 text-gray-400">No fabric needs for {fabricTab === 'pre_production' ? 'pre-production' : 'in-production'} orders</div>;
                }
                const formatType = (t: string) => t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '-';
                return (
                  <div className="space-y-4">
                    {items.map((fab: any) => (
                      <div key={`${fab.fabricType}-${fab.fabricColor}`} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-gray-900">{formatType(fab.fabricType)}</span>
                            <span className="text-gray-500">—</span>
                            <span className="font-medium text-gray-700">{fab.fabricColor || 'No Color'}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-500">{fab.screenCount} screen{fab.screenCount !== 1 ? 's' : ''}</span>
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 font-bold rounded-full">
                              {fab.totalYards} yds
                            </span>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                              <th className="px-4 py-2">Order</th>
                              <th className="px-4 py-2">Dealer</th>
                              <th className="px-4 py-2">Status</th>
                              <th className="px-4 py-2 text-right">Screens</th>
                              <th className="px-4 py-2 text-right">Yards</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fab.orders.map((ord: any) => (
                              <tr key={ord.orderNumber} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  <a href={`/orders/${ord.orderNumber}`} className="text-blue-600 hover:underline font-medium">{ord.orderNumber}</a>
                                </td>
                                <td className="px-4 py-2 text-gray-700">{ord.company}</td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    ord.status === 'in_production' ? 'bg-blue-100 text-blue-700' :
                                    ord.status === 'pending_release' ? 'bg-yellow-100 text-yellow-700' :
                                    ord.status === 'submitted' ? 'bg-orange-100 text-orange-700' :
                                    ord.status === 'approved' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {ord.status.replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right">{ord.screens}</td>
                                <td className="px-4 py-2 text-right font-medium">{ord.yards}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      ) : (
      <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 print:grid-cols-5 print:mb-3 print:gap-2">
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3">
          <div className="text-2xl font-bold text-red-700">{data?.totalShortageItems || 0}</div>
          <div className="text-xs text-red-600 font-medium">Parts Short</div>
        </div>
        <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-3">
          <div className="text-2xl font-bold text-orange-700">{data?.totalOrdersAffected || 0}</div>
          <div className="text-xs text-orange-600 font-medium">Orders Blocked</div>
        </div>
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-3">
          <div className="text-2xl font-bold text-yellow-700">{totalUnordered}</div>
          <div className="text-xs text-yellow-600 font-medium">Need Ordering</div>
        </div>
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-3">
          <div className="text-2xl font-bold text-blue-700">{filteredItems.filter(i => i.onOrder > 0).length}</div>
          <div className="text-xs text-blue-600 font-medium">Already on PO</div>
        </div>
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-3">
          <div className="text-2xl font-bold text-green-700">${totalEstCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="text-xs text-green-600 font-medium">Est. Purchase Order</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4 print:hidden">
        <input
          type="text"
          placeholder="Search parts, suppliers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] max-w-sm px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <select
          value={groupBy}
          onChange={e => setGroupBy(e.target.value as any)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
        >
          <option value="supplier">By Supplier</option>
          <option value="group">By Assembly</option>
          <option value="none">No Grouping</option>
        </select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <span className="text-xs text-gray-500 ml-1 mr-1">All lines:</span>
          {DAY_OPTIONS.map(d => (
            <button
              key={d}
              onClick={() => handleGlobalDaysChange(d)}
              className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                globalDays === d ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyUnordered}
            onChange={e => setShowOnlyUnordered(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-gray-700">Hide ordered</span>
        </label>
        {hiddenParts.size > 0 && (
          <button
            onClick={() => setHiddenParts(new Set())}
            className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded text-gray-600"
          >
            Show {hiddenParts.size} removed
          </button>
        )}
        <button
          onClick={() => { setShowAddModal(true); setAddSearch(''); setAddResults([]); }}
          className="px-3 py-2 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg font-medium flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
          Add Item
        </button>
      </div>

      {/* No shortages */}
      {filteredItems.length === 0 && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-8 text-center">
          <div className="text-4xl mb-2">&#10003;</div>
          <div className="text-xl font-bold text-green-700">{showOnlyUnordered ? 'All Shortages Have POs' : 'All Clear'}</div>
          <div className="text-green-600 mt-1">{showOnlyUnordered ? 'Every shortage item is on a purchase order' : 'No inventory shortages across pipeline orders'}</div>
        </div>
      )}

      {/* Shortage Tables grouped by supplier */}
      {groupedItems().map((group, gi) => {
        const includedInGroup = group.items.filter(i => i.needToOrder > 0 && i.inventoryItemId && !excludedFromPO.has(i.partNumber));
        return (
          <div key={gi} className="mb-6 print:mb-4">
            {group.label && (
              <div className="flex items-center justify-between border-b-2 border-gray-300 pb-1 mb-2">
                <h2 className="text-lg font-bold text-gray-800">
                  {group.label}
                  <span className="text-sm font-normal text-gray-500 ml-2">({group.items.length} parts)</span>
                </h2>
                {includedInGroup.length > 0 && (
                  <button
                    onClick={() => createPOForSupplier(group.label, group.items)}
                    disabled={creating}
                    className="px-3 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded font-bold disabled:opacity-50 print:hidden"
                  >
                    {creating ? '...' : `Create PO (${includedInGroup.length} items)`}
                  </button>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse print:text-xs table-fixed">
                <thead>
                  <tr className="bg-gray-800 text-white print:bg-gray-300 print:text-black">
                    <th className="px-1 py-1.5 text-center font-semibold w-[30px] print:hidden" title="Include in PO">
                      <input
                        type="checkbox"
                        checked={group.items.every(i => !excludedFromPO.has(i.partNumber))}
                        onChange={e => {
                          const checked = e.target.checked;
                          setExcludedFromPO(prev => {
                            const next = new Set(prev);
                            group.items.forEach(i => {
                              if (checked) next.delete(i.partNumber);
                              else next.add(i.partNumber);
                            });
                            return next;
                          });
                        }}
                        className="rounded border-gray-400 cursor-pointer"
                        title="Select all in group"
                      />
                    </th>
                    <th className="px-2 py-1.5 text-center font-semibold w-[50px]">Img</th>
                    <th className="px-2 py-1.5 text-left font-semibold w-[70px]">Part #</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Description</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-orange-300 w-[50px]" title="Lead time in days — editable, saves to inventory">Lead</th>
                    <th className="px-2 py-1.5 text-right font-semibold w-[55px]">Stock</th>
                    <th className="px-2 py-1.5 text-right font-semibold w-[55px]">Short</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-blue-300 w-[55px]">On PO</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-purple-300 bg-gray-900 w-[60px]" title="Coverage days — controls Used & Order Qty per line">Days</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-green-300 w-[65px]" title="Projected consumption for selected days | weekly burn rate">Used</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-yellow-300 bg-gray-900 w-20">ORDER QTY</th>
                    <th className="px-2 py-1.5 text-right font-semibold w-20">Unit $</th>
                    <th className="px-2 py-1.5 text-right font-semibold w-20">Line Total</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-cyan-300 bg-cyan-900 w-20" title="How many days your order qty will last at current burn rate">Supply Coverage</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Status</th>
                    <th className="px-1 py-1.5 text-center font-semibold w-[30px] print:hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item, i) => {
                    const isOrdered = item.onOrder >= item.shortage;
                    const isPartial = item.onOrder > 0 && item.onOrder < item.shortage;
                    const lineDays = getLineDays(item);
                    const usedAmt = getUsedAmount(item);
                    const lastsDays = getLastsDays(item);
                    const hasLineOverride = overrides[item.partNumber]?.days !== undefined;
                    const isExcluded = excludedFromPO.has(item.partNumber);
                    return (
                      <tr
                        key={item.partNumber}
                        className={`border-b border-gray-200 ${
                          isExcluded ? 'opacity-40' :
                          item.urgency === 'critical' && !isOrdered ? 'bg-red-50' :
                          isOrdered ? 'bg-green-50' : isPartial ? 'bg-yellow-50' :
                          i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } hover:bg-blue-50 transition-colors`}
                      >
                        {/* PO include checkbox */}
                        <td className="px-1 py-1 text-center print:hidden">
                          <input
                            type="checkbox"
                            checked={!excludedFromPO.has(item.partNumber)}
                            onChange={() => togglePOInclusion(item.partNumber)}
                            className="rounded border-gray-300 cursor-pointer"
                            title={excludedFromPO.has(item.partNumber) ? 'Click to include in PO' : 'Click to exclude from PO'}
                          />
                        </td>
                        {/* Image */}
                        <td className="px-1 py-1 text-center">
                          {item.imageFilename ? (
                            <img
                              src={item.imageFilename.startsWith('http') ? item.imageFilename : `/uploads/inventory/${item.imageFilename}`}
                              alt=""
                              className="w-10 h-10 object-cover rounded border border-gray-300 mx-auto print:w-8 print:h-8"
                              onError={e => {
                                const el = e.currentTarget;
                                el.style.display = 'none';
                                if (el.nextElementSibling) (el.nextElementSibling as HTMLElement).style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="w-10 h-10 bg-gray-100 rounded border border-gray-200 items-center justify-center mx-auto text-gray-300 text-[10px] print:w-8 print:h-8"
                            style={{ display: item.imageFilename ? 'none' : 'flex' }}
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V5.25a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v14.25a1.5 1.5 0 001.5 1.5z" /></svg>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 font-mono font-bold text-gray-900 whitespace-nowrap">{item.partNumber}</td>
                        <td className="px-2 py-1.5 text-gray-700 max-w-[220px]">
                          <span className="truncate text-xs block">{item.description}</span>
                        </td>
                        {/* Lead time column */}
                        <td className="px-1 py-1 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            <input
                              type="number"
                              min={0}
                              max={365}
                              value={getLeadTime(item)}
                              onChange={e => {
                                const v = parseInt(e.target.value) || 0;
                                setOverrideField(item.partNumber, 'leadTime', v);
                                setOverrides(prev => {
                                  const existing = prev[item.partNumber] || {};
                                  const { qty: _q, ...rest } = existing;
                                  return { ...prev, [item.partNumber]: { ...rest, leadTime: v } };
                                });
                              }}
                              onBlur={e => {
                                const v = parseInt(e.target.value) || 0;
                                if (v !== item.leadTimeDays) {
                                  saveLeadTime(item, v);
                                }
                              }}
                              className={`w-10 px-0.5 py-0.5 text-xs text-center rounded border font-bold ${
                                getLeadTime(item) !== item.leadTimeDays
                                  ? 'border-orange-400 bg-orange-50 text-orange-700'
                                  : 'border-gray-200 bg-gray-50 text-gray-600'
                              } print:border-0 print:bg-transparent`}
                              title="Lead time in days — saves to inventory on change"
                            />
                            <span className="text-[9px] text-gray-400">d</span>
                          </div>
                        </td>
                        <td className={`px-2 py-1.5 text-right font-medium ${item.currentStock === 0 ? 'text-red-700 font-bold' : 'text-gray-600'}`}>
                          {item.currentStock === 0 ? 'OUT' : item.currentStock}
                        </td>
                        <td className="px-2 py-1.5 text-right font-bold text-red-700">{item.shortage}</td>
                        {/* On PO */}
                        <td className="px-2 py-1.5 text-right">
                          {item.onOrder > 0 ? (
                            <span className="text-blue-700 font-bold cursor-help" title={item.openPOs.map(p => `${p.poNumber}: ${p.qtyOrdered - p.qtyReceived} remaining${p.expectedDate ? ` (ETA: ${new Date(p.expectedDate).toLocaleDateString()})` : ''}`).join('\n')}>
                              {item.onOrder}
                            </span>
                          ) : (
                            <span className="text-gray-300">--</span>
                          )}
                        </td>
                        {/* Per-line day selector */}
                        <td className="px-0.5 py-0.5 text-center print:hidden">
                          <select
                            value={lineDays}
                            onChange={e => {
                              const v = parseInt(e.target.value);
                              if (v === globalDays) {
                                // Reset to global — remove per-line override
                                setOverrides(prev => {
                                  const next = { ...prev };
                                  if (next[item.partNumber]) {
                                    const { days: _d, qty: _q, ...rest } = next[item.partNumber];
                                    if (Object.keys(rest).length > 0) {
                                      next[item.partNumber] = rest;
                                    } else {
                                      delete next[item.partNumber];
                                    }
                                  }
                                  return next;
                                });
                              } else {
                                // Also clear qty override so it recalculates for new days
                                setOverrides(prev => {
                                  const existing = prev[item.partNumber] || {};
                                  const { qty: _q, ...rest } = existing;
                                  return { ...prev, [item.partNumber]: { ...rest, days: v } };
                                });
                              }
                            }}
                            className={`w-14 px-0.5 py-1 text-xs rounded border text-center font-bold ${
                              hasLineOverride
                                ? 'border-purple-400 bg-purple-50 text-purple-700'
                                : 'border-gray-300 bg-white text-gray-600'
                            }`}
                            title={`Coverage: ${lineDays} days${hasLineOverride ? ' (custom)' : ' (global default)'}`}
                          >
                            {DAY_OPTIONS.map(d => (
                              <option key={d} value={d}>{d}d</option>
                            ))}
                          </select>
                        </td>
                        {/* Print-only days column */}
                        <td className="hidden print:table-cell px-2 py-1.5 text-center text-xs font-bold">
                          {lineDays}d
                        </td>
                        {/* Used in selected period */}
                        <td className="px-2 py-1.5 text-right" title={item.dailyRate > 0 ? `~${usedAmt} used in ${lineDays}d (from ${item.dailyRate.toFixed(1)}/day avg)\nActual 90d: ${item.consumed90d}\n${item.weeklyRate}/week | ${item.monthlyRate}/month` : 'No consumption history'}>
                          {item.dailyRate > 0 ? (
                            <div>
                              <span className="font-bold text-gray-800">{usedAmt}</span>
                              <div className="text-[10px] text-gray-400">{item.weeklyRate}/wk</div>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-[10px]">no history</span>
                          )}
                        </td>
                        {/* Editable ORDER qty */}
                        <td className="px-1 py-0.5 text-right bg-red-50 print:bg-red-100">
                          <div className="flex items-center justify-end gap-0.5">
                            <input
                              type="number"
                              min={0}
                              value={getOrderQty(item)}
                              onChange={e => setOverrideField(item.partNumber, 'qty', parseInt(e.target.value) || 0)}
                              className="w-12 px-1 py-1 text-right font-bold text-red-700 border border-red-300 rounded text-sm bg-white focus:ring-2 focus:ring-red-400 focus:border-red-400 print:border-0 print:bg-transparent print:w-auto"
                            />
                            {isBulkItem(item) && (
                              <span className="text-[9px] text-gray-500 whitespace-nowrap">{getOrderUnit(item)}</span>
                            )}
                          </div>
                        </td>
                        {/* Editable unit cost — per roll/stick for bulk, per each otherwise */}
                        <td className="px-1 py-0.5 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <span className="text-gray-400 text-xs">$</span>
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={getUnitCost(item).toFixed(2)}
                              onChange={e => setOverrideField(item.partNumber, 'unitCost', parseFloat(e.target.value) || 0)}
                              className="w-16 px-1 py-1 text-right text-gray-700 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-blue-400 print:border-0 print:bg-transparent print:w-auto"
                            />
                          </div>
                          {isBulkItem(item) && (
                            <div className="text-[8px] text-gray-400 text-right">/{getOrderUnit(item).replace(/s$/, '')}</div>
                          )}
                        </td>
                        {/* Line total */}
                        <td className="px-2 py-1.5 text-right font-medium text-gray-800 text-xs whitespace-nowrap">
                          ${(getOrderQty(item) * getUnitCost(item)).toFixed(2)}
                        </td>
                        {/* How long this order lasts — SUPPLY COVERAGE */}
                        <td className="px-2 py-1.5 text-center">
                          {lastsDays !== null ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className={`font-black text-sm ${
                                lastsDays >= 90 ? 'text-green-600' :
                                lastsDays >= 60 ? 'text-green-500' :
                                lastsDays >= 30 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {lastsDays >= 30 ? `${(lastsDays / 30).toFixed(1)}mo` : `${lastsDays}d`}
                              </span>
                              <span className="text-[9px] text-gray-400">{lastsDays}d</span>
                              {/* Mini bar */}
                              <div className="w-12 h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    lastsDays >= 90 ? 'bg-green-500' :
                                    lastsDays >= 60 ? 'bg-green-400' :
                                    lastsDays >= 30 ? 'bg-yellow-400' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(100, (lastsDays / 120) * 100)}%` }}
                                />
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-[10px]">--</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {isOrdered ? (
                            <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-bold">ON PO</span>
                          ) : isPartial ? (
                            <span className="inline-block px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-bold">PARTIAL</span>
                          ) : item.urgency === 'critical' ? (
                            <span className="inline-block px-1.5 py-0.5 bg-red-600 text-white rounded-full text-[10px] font-bold">CRITICAL</span>
                          ) : item.urgency === 'warning' ? (
                            <span className="inline-block px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-[10px] font-bold">WARNING</span>
                          ) : (
                            <span className="inline-block px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold">NEED</span>
                          )}
                        </td>
                        {/* Remove button */}
                        <td className="px-0 py-0.5 text-center print:hidden">
                          <button
                            onClick={() => setHiddenParts(prev => new Set([...prev, item.partNumber]))}
                            className="text-gray-300 hover:text-red-500 transition-colors"
                            title="Remove from list"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-400 print:bg-gray-200">
                    <td className="print:hidden"></td>
                    <td className="px-2 py-1.5" colSpan={5}>{group.label || 'TOTAL'}</td>
                    <td className="px-2 py-1.5 text-right text-red-700">{group.items.reduce((s, i) => s + i.shortage, 0)}</td>
                    <td className="px-2 py-1.5 text-right text-blue-700">{group.items.reduce((s, i) => s + i.onOrder, 0)}</td>
                    <td className="px-2 py-1.5"></td>
                    <td className="px-2 py-1.5 text-right text-gray-500">{group.items.reduce((s, i) => s + getUsedAmount(i), 0)}</td>
                    <td className="px-2 py-1.5 text-right text-red-700">{group.items.reduce((s, i) => s + getOrderQty(i), 0)}</td>
                    <td className="px-2 py-1.5"></td>
                    <td className="px-2 py-1.5 text-right text-gray-900">${group.items.reduce((s, i) => s + getOrderQty(i) * getUnitCost(i), 0).toFixed(2)}</td>
                    <td className="px-2 py-1.5" colSpan={2}></td>
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}

      {/* Print footer */}
      <div className="hidden print:block text-xs text-gray-400 mt-4 border-t pt-2">
        Generated: {new Date().toLocaleString()} | Keder Factory - What to Order Report
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Add Item to Order List</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="Search by part number, description, or supplier..."
                value={addSearch}
                onChange={e => { setAddSearch(e.target.value); searchInventory(e.target.value); }}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {addSearching && (
                <div className="text-center py-4 text-gray-400">Searching...</div>
              )}
              {!addSearching && addSearch.length >= 2 && addResults.length === 0 && (
                <div className="text-center py-4 text-gray-400">No items found</div>
              )}
              {addResults.map((inv: any) => (
                <div
                  key={inv.id || inv.part_number}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {(inv.image_filename || inv.imageFilename) ? (
                      <img
                        src={(() => { const f = inv.image_filename || inv.imageFilename; return f.startsWith('http') ? f : `/uploads/inventory/${f}`; })()}
                        alt=""
                        className="w-10 h-10 object-cover rounded border border-gray-200"
                        onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-gray-300">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-sm">{inv.part_number}</span>
                        <span className="text-xs text-gray-400">{inv.supplier_name || inv.supplierName || ''}</span>
                      </div>
                      <div className="text-xs text-gray-600 truncate">{inv.description}</div>
                      <div className="text-[10px] text-gray-400">
                        Stock: {inv.quantity_on_hand ?? inv.quantityOnHand ?? 0} | Cost: ${(inv.unit_cost || inv.unitCost || 0).toFixed(2)}
                        {(inv.lead_time_days || inv.leadTimeDays) ? ` | Lead: ${inv.lead_time_days || inv.leadTimeDays}d` : ''}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => addItemToList(inv)}
                    className="ml-2 px-3 py-1.5 text-xs bg-green-600 text-white hover:bg-green-700 rounded font-bold flex-shrink-0"
                  >
                    + Add
                  </button>
                </div>
              ))}
            </div>
            {manualItems.length > 0 && (
              <div className="p-3 border-t bg-gray-50 text-xs text-gray-500">
                {manualItems.length} item{manualItems.length !== 1 ? 's' : ''} added to order list
              </div>
            )}
          </div>
        </div>
      )}
    </>
    )}
    </div>
  );
}
